import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import type React from "react";
import { useState } from "react";
import type { Information } from "@/shared/lib/api/api-client";

interface InformationSourceDialogProps {
	open: boolean;
	info: Information | null;
	onClose: () => void;
}

export default function InformationSourceDialog({
	open,
	info,
	onClose,
}: InformationSourceDialogProps): React.ReactElement {
	const [copyTooltipBody, setCopyTooltipBody] = useState("コピー");
	const [copyTooltipTitle, setCopyTooltipTitle] = useState("コピー");

	if (!info) return <div />;

	const handleCopyBody = () => {
		navigator.clipboard.writeText(info.body);
		setCopyTooltipBody("コピーしました");
		setTimeout(() => setCopyTooltipBody("コピー"), 2000);
	};

	const handleCopyTitle = () => {
		navigator.clipboard.writeText(info.title);
		setCopyTooltipTitle("コピーしました");
		setTimeout(() => setCopyTooltipTitle("コピー"), 2000);
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 2,
				},
			}}
		>
			<DialogTitle sx={{ pb: 1.5 }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between">
					<Typography variant="h6" component="div">
						ソースを表示
					</Typography>
					<IconButton
						aria-label="close"
						onClick={onClose}
						size="small"
						sx={{
							color: "grey.600",
							"&:hover": {
								backgroundColor: "grey.100",
							},
						}}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent sx={{ px: 3, pb: 3 }}>
				<Stack spacing={2.5}>
					<Box>
						<Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
							<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
								タイトル
							</Typography>
							<Tooltip title={copyTooltipTitle} placement="top">
								<IconButton
									onClick={handleCopyTitle}
									size="small"
									sx={{
										color: "grey.600",
										"&:hover": {
											backgroundColor: "grey.100",
										},
									}}
								>
									<ContentCopyIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</Stack>
						<Paper
							variant="outlined"
							sx={{
								p: 1.5,
								backgroundColor: "grey.50",
								border: "1px solid",
								borderColor: "grey.200",
								borderRadius: 1,
							}}
						>
							<Typography
								variant="body2"
								sx={{
									wordBreak: "break-word",
									color: "text.primary",
								}}
							>
								{info.title}
							</Typography>
						</Paper>
					</Box>

					<Box>
						<Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
							<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
								本文
							</Typography>
							<Tooltip title={copyTooltipBody} placement="top">
								<IconButton
									onClick={handleCopyBody}
									size="small"
									sx={{
										color: "grey.600",
										"&:hover": {
											backgroundColor: "grey.100",
										},
									}}
								>
									<ContentCopyIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</Stack>
						<Paper
							variant="outlined"
							sx={{
								p: 1.5,
								backgroundColor: "grey.50",
								border: "1px solid",
								borderColor: "grey.200",
								borderRadius: 1,
								maxHeight: 400,
								overflow: "auto",
							}}
						>
							<Typography
								component="pre"
								sx={{
									fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
									fontSize: "13px",
									lineHeight: 1.6,
									color: "text.primary",
									margin: 0,
									whiteSpace: "pre-wrap",
									wordBreak: "break-word",
								}}
							>
								{info.body}
							</Typography>
						</Paper>
					</Box>
				</Stack>
			</DialogContent>
		</Dialog>
	);
}
